import { isNull, parseClipId, getClipTime } from 'components/app/_helpers/_app.helper';
import {
  Clip,
  LinkedGroup,
  LinkedGroupId,
  LinkedClips,
  UNLINKED_ID,
} from 'components/app/editor/_mediator/editor.slice';
const ERR_ID = 'editor-linking.helper.ts';

export const selectLinkedGroup = (linkedGroupId: LinkedGroupId | undefined, linkedClips: LinkedClips): LinkedGroup => {
  if (isNull(linkedClips)) {
    console.warn(`[${ERR_ID}] selectLinkedGroup: linkedGroups are not defined`);
    return [];
  }
  if (linkedGroupId === 0) {
    console.error(`[${ERR_ID}] selectLinkedGroup: linkedGroupId can't be 0`);
    return [];
  }
  if (linkedGroupId === undefined) {
    console.error(`[${ERR_ID}] selectLinkedGroup: linkedGroupId is not defined`);
    return [];
  }
  const result = linkedClips[linkedGroupId];
  if (!result) {
    console.error(`[${ERR_ID}] selectLinkedGroup: linkedGroup '${linkedGroupId}' is not found.`, {
      linkedGroupId,
      linkedClips,
    });
    return [];
  }
  return result;
};

export const generateLinkId = (linkedClips: LinkedClips) => {
  if (isNull(linkedClips)) {
    console.error(`[${ERR_ID}] generateLinkId: linkedGroups are not defined`);
    return 0;
  }
  const ids = Object.keys(linkedClips)
    .map((el) => Number(el))
    .sort((a, b) => b - a);
  return ids.length ? ids[0] + 1 : 1;
};

export const getLinkData = (clip: Clip, linkedClips: LinkedClips) => {
  if (clip.clipGroupId === UNLINKED_ID) return null;

  const group = selectLinkedGroup(clip.clipGroupId, linkedClips);
  if (!group) {
    console.error(`[${ERR_ID}] getLinkData: linkedGroup not found`);
    return null;
  }
  const index = group.findIndex((id) => id === clip.clipId);
  if (index === -1) {
    console.error(`[${ERR_ID}] getLinkData: index not found`);
    return null;
  }
  const isLastInGroup = index === group.length - 1;
  const isFirstInGroup = index === 0;

  return { group, index, isLastInGroup, isFirstInGroup };
};

// Checks if new clips will be included in an existing linked group
export const findLinkByClipTime = (
  clipTime: number,
  linkedClips: LinkedClips,
  selectedFile: FileId
): LinkedGroupId | undefined => {
  const linkingGroup = Object.entries(linkedClips).find(([, linkedGroup]) => {
    const { mediaFileId } = parseClipId(linkedGroup[0]);
    if (selectedFile !== mediaFileId) return;
    return isTimeGetIntoGroup([clipTime], linkedGroup);
  });
  return linkingGroup ? Number(linkingGroup[0]) : undefined;
};

export const isTimeGetIntoGroup = (time: Seconds[], linkedGroup: LinkedGroup) => {
  const clipFirst = parseClipId(linkedGroup[0]);
  const clipLast = parseClipId(linkedGroup[linkedGroup.length - 1]);
  return time.some((value) => {
    if (value > getClipTime(clipFirst)! && value < getClipTime(clipLast)!) return true;
    if (value === getClipTime(clipFirst)) return true;
    if (value === clipLast.startTime) return true;
  });
};

// Checks if a group of clips can be linked
// IMPORTANT: clips must be sorted by sortClips()
export const isLinkingPossible = (clips: ClipId[]) => {
  const error = clips.some((clipId, index) => {
    const clipData = parseClipId(clipId);
    if (index < clips.length - 1) {
      const nextClip = parseClipId(clips[index + 1]);
      if (
        (isNull(clipData.endTime) && isNull(nextClip.startTime)) ||
        (!isNull(clipData.endTime) && !isNull(nextClip.startTime) && clipData.endTime !== nextClip.startTime)
      )
        return true;
    }
  });
  return !error;
};

export const isLinksValid = (linkedClips: LinkedClips) => {
  const error = Object.values(linkedClips).some((clips) => !isLinkingPossible(clips));
  return error ? false : true;
};

export const speedIsEnabled = (clip: Clip, linkedClips: LinkedClips) => {
  const { clipId, startTime, endTime, clipGroupId } = clip;
  if (!isNull(startTime) && !isNull(endTime)) return false;
  if (clipGroupId) {
    const group = selectLinkedGroup(clipGroupId, linkedClips);
    const index = group.findIndex((id) => clipId === id);
    const first = index === 0;
    const last = index === group.length - 1;
    if (!isNull(startTime) && !last) return false;
    if (!isNull(endTime) && !first) return false;
  }
  return true;
};
